import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Checkbox,
    Grid,
    Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import ButtonComponent from "../../Components/Common/button/Button";
import { useSelector } from "react-redux";
import { set } from "lodash";

const forecastTermOptions = ["Short Term", "Long Term"];

const EditReminderDialog = ({
    open,
    onClose,
    data,
    refreshTable,
    roleOptions
}) => {
    const [form, setForm] = useState({
        dataElement: "",
        forecastTerm: null,
        reminderPeriod: null,
        ndays: "",
        roles: [],
        emails: "",
        active: true,
    });

    const userId = useSelector((state) => state.user?.userData?.id);

    const [selectedBottler, setSelectedBottler] = useState(null);
    const [selectedBottlerOwnerCode, setSelectedBottlerOwnerCode] = useState("");

    const [selectedPartners, setSelectedPartners] = useState([]);
    const [selectedPlants, setSelectedPlants] = useState([]);
    const [selectedCustomerCountry, setSelectedCustomerCountry] = useState([]);

    const [reminderPeriodWithDeadlineOptions, setReminderPeriodWithDeadlineOptions] = useState([]);
    const [reminderPeriodWithoutDeadlineOptions, setReminderPeriodWithoutDeadlineOptions] = useState([]);
    const reminderPeriodOptions =
        form.dataElement === "Bottler Concentrate Forecast"
            ? reminderPeriodWithDeadlineOptions
            : reminderPeriodWithoutDeadlineOptions;
    const [bottlerOption, setBottlerOption] = useState([]);
    const [partnerOption, setPartnerOption] = useState([]);
    const [plantOption, setPlantOption] = useState([]);
    const [customerCountryOption, setCustomerCountryOption] = useState([]);

    const [searchText, setSearchText] = useState("");
    const [searchForecastPartnerText, setSearchForecastPartnerText] = useState("");

    const [customerCountryNoOptionsText, setCustomerCountryNoOptionsText] =
        useState("Select Bottler to view Customer Country");

    const [plantNoOptionsText, setPlantNoOptionsText] =
        useState("Select Forecast Partner to view Plants");

    const selectAllCustomerCountryOption = {
        profitCenterDetails: "All Customer Country",
        isSelectAll: true,
    };

    const optionsWithSelectAllCustomerCountries =
        customerCountryOption.length > 0
            ? [selectAllCustomerCountryOption, ...customerCountryOption]
            : [];

    useEffect(() => {
        if (!open) return;

        fetchReminderOption();
    }, [open]);

    // Fetch Reminder Period Options
    const fetchReminderOption = async () => {
        try {
            const response = await ApiService.get(
                `/v1/reminder-configurations/get-reminder-periods`,
            );
            setReminderPeriodWithDeadlineOptions(response.data || []);

            const withoutDeadlineOptions = (response.data || []).filter(
                (option) => !option.code.startsWith("DEADLINE")
            );
            setReminderPeriodWithoutDeadlineOptions(withoutDeadlineOptions);

        } catch (error) {
            console.error("Failed to fetch reminder period options:", error);
            setReminderPeriodWithDeadlineOptions([]);
            setReminderPeriodWithoutDeadlineOptions([]);
        }
    };

    /* ================= Populate ================= */
    useEffect(() => {
        if (!data) return;

        const options =
            data.dataElement === "Bottler Concentrate Forecast"
                ? reminderPeriodWithDeadlineOptions
                : reminderPeriodWithoutDeadlineOptions;

        setForm({
            dataElement: data.dataElement,
            forecastTerm:
                data.forecastTerm === 1
                    ? "Short Term"
                    : data.forecastTerm === 2
                        ? "Long Term"
                        : null,

            reminderPeriod:
                options.find((r) => r.code === data.reminderPeriod) || null,

            ndays: data.ndays ?? "",

            roles: roleOptions.filter((r) =>
                data.roles?.includes(r.code)
            ),

            emails: data.userEmails || "",

            active: data.active,
            adminAloneEditable: data.adminAloneEditable || false
        });
        const output = data.customerCountryList?.map(country => ({
            bottlerOwnerCode: data.bottlerOwnerCode,
            profitCenterDetails: country
        })) || [];

        setSelectedBottlerOwnerCode(data.bottlerOwnerCode || "");
        setSelectedCustomerCountry(output);

        const customerIds = (data.customerInfoList || []).map(
            item => item.customerID
        );
        setSelectedPartners(customerIds);

        const plantInfoList = (data.customerInfoList || []).flatMap(customer =>
            (customer?.locationIds || []).map(location => ({
                customerDetails: customer?.customerID?.split("-")[0] || "",
                locationDetails: location
            }))

        );
        setSelectedPlants(plantInfoList);

        if (data.bottler && data.bottler.length > 0) {
            if (form.dataElement === "Bottler Concentrate Forecast") {
                fetchBottlerConcentrateForecastPartnerOptions(data.bottler);
                fetchPlantOptions(customerIds);
            } else {
                fetchBottlerOwnerCode(data.bottler);
            }
        }


    }, [
        data,
        reminderPeriodWithDeadlineOptions,
        reminderPeriodWithoutDeadlineOptions,
        roleOptions
    ]);

    const isForecastEditable =
        form.dataElement === "Bottler Concentrate Forecast";

    const fetchBottlerSelection = async (dataElement) => {
        const endpointMap = {
            "Bottler Concentrate Forecast":
                `/v1/admin/userManagement/bottler-by-userid?userId=${userId}`,

            "Bottler Market Forecast":
                `/v1/user-pc-country-access/get-bottlers-by-user-id/marketforecast?userId=${userId}`,

            "FG Inventory":
                `/v1/user-pc-country-access/get-bottlers-by-user-id/fgInventory?userId=${userId}`,

            "Bottler Bussiness Plan":
                `/v1/user-pc-country-access/get-bottlers-by-user-id/businessPlan?userId=${userId}`,

            "Concentrate Inventory":
                `/v1/user-pc-country-access/get-bottlers-by-user-id/concentrateInventory?userId=${userId}`,

            "Production Requirement":
                `/v1/user-pc-country-access/get-bottlers-by-user-id/productionRequirement?userId=${userId}`,
        };

        const response = await ApiService.get(endpointMap[dataElement]);

        const options = Array.isArray(response.data) ? response.data : [];

        setBottlerOption(options);

        return options;
    };

    useEffect(() => {
        if (!data || !open) return;

        const loadBottler = async () => {
            const options = await fetchBottlerSelection(data.dataElement);

            const selected = options.find(
                (option) => option === data.bottler
            );

            console.log("Options:", options);
            console.log("Saved Bottler:", data.bottler);
            console.log("Matched:", options.find(option => option === data.bottler));

            setSelectedBottler(selected || null);
            setSelectedBottlerOwnerCode(data.bottlerOwnerCode || "");
        };

        loadBottler();
    }, [data, open]);

    const selectAllForecastPartnerOption = {
        forecastPartnerDetails: "All Forecast Partner",
        isSelectAll: true,
    };

    const optionsWithSelectAllForecastPartners =
        partnerOption.length > 0
            ? [selectAllForecastPartnerOption, ...partnerOption]
            : [];
    const selectAllOption = {
        locationDetails: "All Plants",
        isSelectAll: true,
    };

    const optionsWithSelectAll =
        plantOption.length > 0 ? [selectAllOption, ...plantOption] : [];

    const handleBottlerChange = (event, value) => {
        setSelectedBottler(value || null);

        //clear dependent selections
        setSelectedPartners([]);
        setSelectedPlants([]);
        setSelectedCustomerCountry([]);
        setSelectedBottlerOwnerCode("");

        //clear dependent options
        setCustomerCountryOption([]);
        setPartnerOption([]);
        setPlantOption([]);

        if (value && value.length > 0) {
            if (form.dataElement === "Bottler Concentrate Forecast") {
                fetchBottlerConcentrateForecastPartnerOptions(value);
            } else {
                fetchBottlerOwnerCode(value);
            }
        } else {
            setSelectedBottlerOwnerCode("");
        }
    };

    // Customer Options
    const fetchBottlerConcentrateForecastPartnerOptions = async (bottler) => {
        try {
            const response = await ApiService.get(
                `v1/admin/userManagement/customer-selection-by-bottler?bottler=${encodeURIComponent(bottler)}`,
            );

            setPartnerOption(response.data);

        } catch (error) {
            console.error("Error fetching partner and plant options:", error);
        }
    };

    //Fetch bottler owner code
    const fetchBottlerOwnerCode = async (bottler) => {
        try {
            const response = await ApiService.get(
                `/v1/user-pc-country-access/get-bottler-owner-code-by-bottler?bottler=${encodeURIComponent(bottler)}`
            );

            setSelectedBottlerOwnerCode(
                Array.isArray(response.data) ? response.data.join(", ") : ""
            );

            const endpointMap = {
                "Bottler Market Forecast":
                    "/v1/reminder-configurations/get-country-by-bottler-owner-code/marketforecast",

                "FG Inventory":
                    "/v1/reminder-configurations/get-country-by-bottler-owner-code/fgInventory",

                "Bottler Bussiness Plan":
                    "/v1/reminder-configurations/get-country-by-bottler-owner-code/businessPlan",

                "Concentrate Inventory":
                    "/v1/reminder-configurations/get-country-by-bottler-owner-code/concentrateInventory",

                "Production Requirement":
                    "/v1/reminder-configurations/get-country-by-bottler-owner-code/productionRequirement",
            };

            const url = endpointMap[form.dataElement];


            //fetch customer country options
            fetchCustomerCountryOptions(url, response.data);

        } catch (error) {
            console.error("Error fetching bottler owner code:", error);
            setSelectedBottlerOwnerCode("");
        }
    };

    //Forecast Partner handle
    const handleForecastPartnerChange = (event, newValue) => {
        const isSelectAllClicked = newValue.some((item) => item.isSelectAll);

        setSelectedPlants([]);
        setPlantOption([]);

        if (isSelectAllClicked) {
            if (selectedPartners.length === partnerOption.length) {
                setSelectedPartners([]);
            } else {
                setSelectedPartners(partnerOption)
                fetchPlantOptions(partnerOption);
            }

        } else {
            setSelectedPartners(newValue || []);
            fetchPlantOptions(newValue);
        }
    };

    const fetchPlantOptions = async (partners) => {
        const customerId = partners.map((item) => item.split("-")[0]);

        const payloadData = {
            userId: userId,
            customerIds: customerId,
        };

        try {
            const response = await ApiService.post(
                `v1/reminder-configurations/location-selection-by-customer`,
                payloadData,
            );
            if (response.data && response.data.length > 0) {
                const updatedPlants = response.data.map((plant) => ({
                    ...plant,
                    locationDetails: `${plant.locationDetails}(${plant.customerDetails})`,
                }));
                setPlantOption(updatedPlants);
                setPlantNoOptionsText("Select Forecast Partner to view Plants"); // Reset to default message
            } else {
                setPlantOption([]); // Clear plant options
                setPlantNoOptionsText("No Plants for Select Forecast Partner"); // Update message for empty array
            }
        } catch (error) {
            console.error("Error fetching plant options:", error);
            setPlantOption([]); // Clear plant options in case of error
            setPlantNoOptionsText("Error fetching plant options"); // Optional error message
        }
    };

    //Fetch customer country options
    const fetchCustomerCountryOptions = async (url, bottlerOwnerCodeList) => {
        const payloadData = {
            userId: userId,
            bottlerOwnerCodeList: bottlerOwnerCodeList
        }

        try {
            const response = await ApiService.post(
                url,
                payloadData,
            );

            if (response.data && response.data.length > 0) {
                setCustomerCountryOption(response.data);
                setCustomerCountryNoOptionsText(
                    "Select Bottler to view Customer Country",
                );
            } else {
                setCustomerCountryOption([]);
                setCustomerCountryNoOptionsText(
                    "No Customer Country found for selected Bottler",
                );
            }
        } catch (error) {
            console.error("Error fetching Country options:", error);
            setCustomerCountryOption([]);
            setCustomerCountryNoOptionsText(
                "Error fetching Country options. Please try again.",
            );
        }
    }

    const handleSave = async () => {

        if (!form.reminderPeriod?.code) {
            MessagePopup({
                icon: "warning",
                title: "Validation Error",
                message: "Reminder Period is required",
            });
            return;
        }

        if (!form.ndays || Number(form.ndays) <= 0) {
            MessagePopup({
                icon: "warning",
                title: "Validation Error",
                message: "Number of Days is required and must be greater than 0",
            });
            return;
        }

        if (!form.roles || form.roles.length === 0) {
            MessagePopup({
                icon: "warning",
                title: "Validation Error",
                message: "At least one role must be selected",
            });
            return;
        }
        // Forecast Partner validation
        if (!selectedBottler) {
            MessagePopup({
                icon: "warning",
                title: "Validation Error",
                message: "Bottlers cannot be empty",
            });
            return;
        }

        // Conditional validation based on Data Element
        if (form.dataElement === "Bottler Concentrate Forecast") {

            // Forecast Partner validation
            if (!selectedPartners || selectedPartners.length === 0) {
                MessagePopup({
                    icon: "warning",
                    title: "Validation Error",
                    message: "Forecast Partner cannot be empty",
                });
                return;
            }

            // Plants validation
            if (!selectedPlants || selectedPlants.length === 0) {
                MessagePopup({
                    icon: "warning",
                    title: "Validation Error",
                    message: "Plants cannot be empty",
                });
                return;
            }

        } else {

            // Customer Country validation
            if (!selectedCustomerCountry || selectedCustomerCountry.length === 0) {
                MessagePopup({
                    icon: "warning",
                    title: "Validation Error",
                    message: "Customer Country cannot be empty",
                });
                return;
            }
        }

        if (
            form.dataElement === "Bottler Concentrate Forecast" &&
            isForecastEditable &&
            !form.forecastTerm
        ) {
            MessagePopup({
                icon: "warning",
                title: "Validation Error",
                message: "Forecast Term is required for Bottler Concentrate Forecast",
            });
            return;
        }


        const payload = {
            dataElement: form.dataElement,

            forecastTerm: isForecastEditable
                ? form.forecastTerm === "Short Term"
                    ? 1
                    : form.forecastTerm === "Long Term"
                        ? 2
                        : null
                : null,

            reminderPeriod: form.reminderPeriod?.code,
            ndays: Number(form.ndays),
            active: form.active,
            roles: form.roles?.map((r) => r.code) || [],
            userEmails: Array.isArray(form.emails)
                ? form.emails.join(",")
                : form.emails || "", bottler: selectedBottler,
            bottlerOwnerCode: selectedBottlerOwnerCode,
            customerCountryList: selectedCustomerCountry.map(
                item => item.profitCenterDetails.split("-")[0]
            ),
            adminAloneEditable: form.adminAloneEditable,
            customerInfoList: Object.values(
                (selectedPlants ?? []).reduce((acc, { customerDetails, locationDetails }) => {
                    if (!acc[customerDetails]) {
                        acc[customerDetails] = {
                            customerID: customerDetails,
                            locationIds: []
                        };
                    }

                    acc[customerDetails].locationIds.push(locationDetails.split("-")[0]);

                    return acc;
                }, {})
            )

        };

        try {
            await ApiService.put(
                `/v1/reminder-configurations/update/${data.id}`,
                payload
            );

            MessagePopup({
                icon: "success",
                title: "Updated",
                message: "Reminder updated successfully",
            });

            refreshTable();
            onClose();
        } catch (error) {
            const message =
                typeof error?.response?.data === "string"
                    ? error.response.data
                    : "Failed to update reminder";

            MessagePopup({
                icon: "error",
                title: "Error",
                message,
            });
        }
    };
    return (
        // <Dialog
        //     open={open}
        //     onClose={onClose}
        //     maxWidth="md"
        //     fullWidth
        //     PaperProps={{
        //         sx: {
        //             overflow: "visible",
        //         },
        //     }}
        // >
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: "95vh",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
        >
            <DialogTitle>Edit Reminder</DialogTitle>

            {/* <DialogContent
                sx={{
                    pt: 4,
                    pb: 2,
                    overflow: "visible",
                }}
            > */}
            <DialogContent
                sx={{
                    pt: 4,
                    pb: 2,
                    overflowY: "auto",
                    flex: 1,
                }}
            >
                <Grid container spacing={2}>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <TextField
                            label="Data Element*"
                            value={form.dataElement}
                            fullWidth
                            disabled
                            size="small"
                            sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    transform: "translate(14px, -14px) scale(0.75)",
                                },
                            }}
                        />
                    </Grid>


                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={forecastTermOptions}
                            value={form.forecastTerm}
                            onChange={(e, v) =>
                                setForm({ ...form, forecastTerm: v })
                            }
                            disabled={!isForecastEditable}

                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={
                                        isForecastEditable ? "Forecast Term*" : "Forecast Term"
                                    } size="small"
                                    sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                                    InputLabelProps={{
                                        shrink: true,
                                        style: {
                                            transform:
                                                "translate(14px, -14px) scale(0.75)",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>


                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            options={reminderPeriodOptions}
                            getOptionLabel={(o) => o.displayName}
                            value={form.reminderPeriod}
                            onChange={(e, v) =>
                                setForm({ ...form, reminderPeriod: v })
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Reminder Period*"
                                    size="small"
                                    sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                                    InputLabelProps={{
                                        shrink: true,
                                        style: {
                                            transform:
                                                "translate(14px, -14px) scale(0.75)",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Number of Days (0–14)*"
                            type="number"
                            value={form.ndays}
                            onChange={(e) =>
                                setForm({ ...form, ndays: e.target.value })
                            }
                            fullWidth
                            size="small"
                            sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    transform:
                                        "translate(14px, -14px) scale(0.75)",
                                },
                            }}
                        />
                    </Grid>


                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={roleOptions}
                            value={form.roles}
                            getOptionLabel={(o) => o.displayName}
                            isOptionEqualToValue={(o, v) => o.code === v.code}
                            onChange={(e, v) =>
                                setForm({ ...form, roles: v })
                            }
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option.displayName}
                                </li>
                            )}
                            sx={{
                                "& .MuiInputBase-root": {
                                    minHeight: 37.5,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                },
                                "& .MuiAutocomplete-input": {
                                    order: -1,
                                    minWidth: "100px",
                                    flexGrow: 1,
                                },
                                "& .MuiChip-root": {
                                    order: 0,
                                    height: 24,
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Roles*"
                                    size="small"
                                    InputLabelProps={{
                                        shrink: true,
                                        style: {
                                            transform:
                                                "translate(14px, -14px) scale(0.75)",
                                        },
                                    }}
                                />
                            )}
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <TextField
                            label="Email Recipients"
                            fullWidth
                            size="small"
                            placeholder="user@company.com"
                            value={form.emails}
                            onChange={(e) =>
                                setForm({ ...form, emails: e.target.value })
                            }
                            sx={{ "& .MuiInputBase-root": { height: 37.5 } }}
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    transform:
                                        "translate(14px, -14px) scale(0.75)",
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {/* <Autocomplete
                            options={bottlerOption}
                            value={selectedBottler}
                            isOptionEqualToValue={(option, value) => option === value}
                            onChange={handleBottlerChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Bottler*"
                                    size="small"
                                />
                            )}
                        /> */}
                        <TextField
                            label="Bottler*"
                            size="small"
                            value={selectedBottler}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                                style: {
                                    transform: "translate(14px, -14px) scale(0.75)",
                                },
                            }}
                        />
                    </Grid>
                    {!isForecastEditable && (
                        <Grid item xs={12} md={6}>
                            {/* Bottler Owner Code */}
                            <TextField
                                label="Bottler Owner Code"
                                variant="outlined"
                                size="small"
                                value={selectedBottlerOwnerCode || ""}
                                disabled
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                    style: {
                                        transform: "translate(14px, -14px) scale(0.75)",
                                    },
                                }}
                            />
                        </Grid>)}
                    {!isForecastEditable && (
                        <Grid item xs={12} >
                            {/* Customer Country */}
                            <Autocomplete
                                multiple
                                limitTags={1}
                                id="pc-country-autocomplete"
                                options={optionsWithSelectAllCustomerCountries}
                                getOptionLabel={(option) => option.profitCenterDetails}
                                value={selectedCustomerCountry}
                                onChange={(event, newValue, reason) => {
                                    const isSelectAllClicked = newValue.some(
                                        (item) => item.isSelectAll,
                                    );
                                    if (isSelectAllClicked) {
                                        if (
                                            selectedCustomerCountry.length ===
                                            optionsWithSelectAllCustomerCountries.length - 1
                                        ) {
                                            setSelectedCustomerCountry([]);
                                        } else {
                                            setSelectedCustomerCountry(
                                                optionsWithSelectAllCustomerCountries.filter(
                                                    (item) => !item.isSelectAll,
                                                ),
                                            );
                                        }
                                        setSearchText(""); // Clear search text after Select All action
                                    } else {
                                        setSelectedCustomerCountry(newValue);
                                    }
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    // Update search text only when typing or clearing the input
                                    if (reason === "input" || reason === "clear") {
                                        setSearchText(newInputValue);
                                    }
                                }}
                                inputValue={searchText} // Control the input value
                                disableCloseOnSelect
                                filterSelectedOptions={false}
                                noOptionsText={customerCountryNoOptionsText}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            style={{ marginRight: 8 }}
                                            checked={
                                                option.isSelectAll
                                                    ? selectedCustomerCountry.length ===
                                                    optionsWithSelectAllCustomerCountries.length - 1
                                                    : selected
                                            }
                                        />
                                        {option.profitCenterDetails}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Customer Country *"
                                        variant="outlined"
                                        size="small"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                transform: "translate(14px, -14px) scale(0.75)",
                                            },
                                        }}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                maxHeight: 82,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                flexDirection: "row",
                                                overflow: "auto",
                                            },
                                            "& .MuiAutocomplete-input": {
                                                order: -1,
                                                minWidth: "100px",
                                                flexGrow: 1,
                                                position: "relative",
                                            },
                                            "& .MuiChip-root": {
                                                order: 0,
                                            },
                                        }}
                                    />
                                )}
                                renderTags={(selected, getTagProps) =>
                                    selected.map((option, index) => (
                                        <Chip
                                            key={option.profitCenterDetails}
                                            label={option.profitCenterDetails}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />
                        </Grid>)}
                    {isForecastEditable && (
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                limitTags={1}
                                id="partner-autocomplete"
                                options={optionsWithSelectAllForecastPartners}
                                getOptionLabel={(option) =>
                                    option.isSelectAll ? option.forecastPartnerDetails : option
                                }
                                value={selectedPartners}
                                onChange={(event, newValue, reason) => {
                                    const isSelectAllClicked = newValue.some(
                                        (item) => item.isSelectAll,
                                    );
                                    if (isSelectAllClicked) {
                                        if (selectedPartners.length === partnerOption.length) {
                                            setSelectedPartners([]);
                                        } else {
                                            setSelectedPartners(partnerOption);
                                        }
                                        setSearchForecastPartnerText(""); // Clear search text after Select All action
                                    } else {
                                        setSelectedPartners(newValue);
                                    }
                                    handleForecastPartnerChange(event, newValue, reason); // Preserve original handler
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    // Update search text only when typing or clearing the input
                                    if (reason === "input" || reason === "clear") {
                                        setSearchForecastPartnerText(newInputValue);
                                    }
                                }}
                                inputValue={searchForecastPartnerText} // Control the input value
                                disableCloseOnSelect
                                noOptionsText="Select Bottler to view Forecast Partner"
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            style={{ marginRight: 8 }}
                                            checked={
                                                option.isSelectAll
                                                    ? selectedPartners.length === partnerOption.length
                                                    : selected
                                            }
                                        />
                                        {option.isSelectAll ? option.forecastPartnerDetails : option}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Forecast Partner *"
                                        variant="outlined"
                                        size="small"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                transform: "translate(14px, -14px) scale(0.75)",
                                            },
                                        }}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                maxHeight: 82,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                flexDirection: "row",
                                                overflow: "auto",
                                            },
                                            "& .MuiAutocomplete-input": {
                                                order: -1, // Input appears before chips
                                                minWidth: "100px", // Ensure input stays visible
                                                flexGrow: 1,
                                                position: "relative", // Input takes available space
                                            },
                                            "& .MuiChip-root": {
                                                order: 0, // Chips appear after input
                                            },
                                        }}
                                    />
                                )}
                                renderTags={(selected, getTagProps) =>
                                    selected.map((option, index) => (
                                        <Chip
                                            key={option.isSelectAll ? option.forecastPartnerDetails : option}
                                            label={option.isSelectAll ? option.forecastPartnerDetails : option}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />
                        </Grid>)}
                    {isForecastEditable && (
                        <Grid item xs={12} >
                            <Autocomplete
                                multiple
                                limitTags={1}
                                id="plant-autocomplete"
                                options={optionsWithSelectAll}
                                getOptionLabel={(option) => option.locationDetails}
                                value={selectedPlants}
                                onChange={(event, newValue, reason) => {
                                    const isSelectAllClicked = newValue.some(
                                        (item) => item.isSelectAll,
                                    );
                                    if (isSelectAllClicked) {
                                        if (selectedPlants.length === plantOption.length) {
                                            setSelectedPlants([]);
                                        } else {
                                            setSelectedPlants(plantOption);
                                        }
                                        setSearchText(""); // Clear search text after Select All action
                                    } else {
                                        setSelectedPlants(newValue);
                                    }
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    // Update search text only when typing or clearing the input
                                    if (reason === "input" || reason === "clear") {
                                        setSearchText(newInputValue);
                                    }
                                }}
                                inputValue={searchText} // Control the input value
                                disableCloseOnSelect
                                filterSelectedOptions={false}
                                noOptionsText={plantNoOptionsText}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            style={{ marginRight: 8 }}
                                            checked={
                                                option.isSelectAll
                                                    ? selectedPlants.length === plantOption.length
                                                    : selected
                                            }
                                        />
                                        {option.locationDetails}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Plant *"
                                        variant="outlined"
                                        size="small"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                transform: "translate(14px, -14px) scale(0.75)",
                                            },
                                        }}
                                        sx={{
                                            "& .MuiInputBase-root": {
                                                maxHeight: 82,
                                                display: "flex",
                                                flexWrap: "wrap",
                                                alignItems: "center",
                                                flexDirection: "row",
                                                overflow: "auto",
                                            },
                                            "& .MuiAutocomplete-input": {
                                                order: -1, // Input appears before chips
                                                minWidth: "100px", // Ensure input stays visible
                                                flexGrow: 1,
                                                position: "relative", // Input takes available space
                                            },
                                            "& .MuiChip-root": {
                                                order: 0, // Chips appear after input
                                            },
                                        }}
                                    />
                                )}
                                renderTags={(selected, getTagProps) =>
                                    selected.map((option, index) => (
                                        <Chip
                                            key={option.locationDetails}
                                            label={option.locationDetails}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                            />
                        </Grid>)}
                </Grid>
            </DialogContent>


            <DialogActions sx={{ pb: 2, pr: 3 }}>
                <ButtonComponent
                    label="Cancel"
                    bgColor="grey"
                    color="white"
                    maxWidth="120px"
                    onClick={onClose}
                />
                <ButtonComponent
                    label="Save"
                    bgColor="black"
                    color="white"
                    maxWidth="120px"
                    onClick={handleSave}
                />
            </DialogActions>
        </Dialog>
    );
};

export default EditReminderDialog;